import { IconX } from "@tabler/icons-react";
import { useCallback, useEffect, useId, useRef } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface DialogProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Footer actions, right-aligned. */
  footer?: ReactNode;
}

/**
 * A small modal dialog: focus is trapped inside, Escape closes it.
 *
 * Args:
 *     title: Dialog heading, also its accessible name.
 *     onClose: Called on Escape, backdrop click or the close button.
 *     children: Dialog body.
 *     footer: Optional footer actions.
 *
 * Returns:
 *     The dialog, portaled to `document.body`.
 */
export function Dialog({
  title,
  onClose,
  children,
  footer,
}: DialogProps): JSX.Element {
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();

  const focusables = useCallback((): HTMLElement[] => {
    const root = panel.current;
    return root ? Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)) : [];
  }, []);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    focusables()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab") {
        return;
      }
      const items = focusables();
      if (items.length === 0) {
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !panel.current?.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      previous?.focus();
    };
  }, [onClose, focusables]);

  return createPortal(
    <div
      className="dialog-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panel}
      >
        <div className="dialog__head">
          <h2 className="dialog__title" id={titleId}>
            {title}
          </h2>
          <button
            type="button"
            className="btn btn--ghost btn--xs"
            onClick={onClose}
            aria-label="Close this dialog"
          >
            <IconX size={14} aria-hidden="true" />
          </button>
        </div>
        <div className="dialog__body">{children}</div>
        {footer ? <div className="dialog__foot">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
